import {
    type NamedWinchPosition,
    WINCH_STATES,
    type WinchLimits,
    type WinchMotionState,
} from './winchMqttTypes';

export function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

export function asFiniteNumber(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }
    if (typeof value === 'string' && value.trim() !== '') {
        const parsed = Number.parseFloat(value);
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
}

export function asBoolean(value: unknown, fallback: boolean): boolean {
    return typeof value === 'boolean' ? value : fallback;
}

export function asWinchState(value: unknown): WinchMotionState | null {
    if (
        value === WINCH_STATES.idle ||
        value === WINCH_STATES.moving ||
        value === WINCH_STATES.descending ||
        value === WINCH_STATES.holding ||
        value === WINCH_STATES.service ||
        value === WINCH_STATES.calibrating
    ) {
        return value;
    }
    return null;
}

export function unwrapData(payload: unknown): Record<string, unknown> | null {
    if (!isRecord(payload)) {
        return null;
    }
    if (isRecord(payload.data)) {
        return payload.data;
    }
    return payload;
}

export function matchNamedPosition(
    positions: NamedWinchPosition[],
    names: string[]
): { distance: number; depth: number } | null {
    const match = positions.find((item) => {
        const key = (item.name ?? item.id).toLowerCase();
        return names.includes(key);
    });
    return match?.position ?? null;
}

export function parseLimits(raw: unknown): WinchLimits | null {
    if (!isRecord(raw) || !isRecord(raw.distance) || !isRecord(raw.depth)) {
        return null;
    }
    const distanceMin = asFiniteNumber(raw.distance.min);
    const distanceMax = asFiniteNumber(raw.distance.max);
    const depthMin = asFiniteNumber(raw.depth.min);
    const depthMax = asFiniteNumber(raw.depth.max);
    if (distanceMin == null || distanceMax == null || depthMin == null || depthMax == null) {
        return null;
    }
    return {
        distance: { min: distanceMin, max: distanceMax },
        depth: { min: depthMin, max: depthMax },
    };
}

/** Status travel envelope is `strictlimits` (`strictLimits` / `strict_limits` aliases). */
export function parseTravelLimits(data: Record<string, unknown>): WinchLimits | null {
    return parseLimits(data.strictlimits ?? data.strictLimits ?? data.strict_limits);
}

export function parseCoords(raw: unknown): { distance: number; depth: number } | null {
    if (!isRecord(raw)) {
        return null;
    }
    const distance = asFiniteNumber(raw.distance) ?? asFiniteNumber(raw.x);
    const depth = asFiniteNumber(raw.depth) ?? asFiniteNumber(raw.y);
    if (distance == null || depth == null) {
        return null;
    }
    return { distance, depth };
}

export function parseSavedPositions(raw: unknown): NamedWinchPosition[] {
    if (!Array.isArray(raw)) {
        return [];
    }
    const positions: NamedWinchPosition[] = [];
    for (const item of raw) {
        if (!isRecord(item)) {
            continue;
        }
        const position = parseCoords(item.position) ?? parseCoords(item);
        const id =
            typeof item.id === 'string' ? item.id : typeof item.name === 'string' ? item.name : '';
        if (position == null || id === '') {
            continue;
        }
        positions.push({
            id,
            name: typeof item.name === 'string' ? item.name : undefined,
            position,
        });
    }
    return positions;
}

export function parseNamedConfigPositions(raw: unknown): NamedWinchPosition[] {
    if (!isRecord(raw)) {
        return [];
    }
    const positions: NamedWinchPosition[] = [];
    for (const [id, value] of Object.entries(raw)) {
        const position = parseCoords(value);
        if (position == null) {
            continue;
        }
        positions.push({ id, position });
    }
    return positions;
}
